/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {getParamsFromPath, getPathnameAndSearch} from './utils'
import {MY_STOKKE_CONFIG_LABELS, MY_STOKKE_CONFIG_LABELS_D2B, SITE_TYPES} from '../constants'

export const getSiteType = (site) =>
    site?.siteType || (site?.isContentOnlySite ? SITE_TYPES.CONTENT_ONLY : SITE_TYPES.ECOM)

export const isContentOnlySite = (site) => getSiteType(site) === SITE_TYPES.CONTENT_ONLY

export const isTransactionalSite = (site) => !isContentOnlySite(site)

/**
 * This functions takes message id and return locale specific translation.
 * @param {string} id is the message identifier
 * @param {string} type is label type
 * @returns {string} locale specific label
 */
export const getMyStokkeLabels = (id, type, formatMessage, isD2BCustomer) => {
    const configData = !isD2BCustomer ? MY_STOKKE_CONFIG_LABELS : MY_STOKKE_CONFIG_LABELS_D2B
    const label = configData?.[id]?.[type] ? formatMessage(configData[id][type]) : undefined
    return label?.startsWith('mystokke.') ? undefined : label
}

/**
 * This functions takes an url and returns a site object,
 * an error will be thrown if no url is passed in or no site is found
 * @param {string} url
 * @returns {object} site - a site object
 */
export const resolveSiteFromUrl = (url) => {
    if (!url) {
        throw new Error('URL is required to find a site object.')
    }
    const {pathname, search} = getPathnameAndSearch(url)
    const path = `${pathname}${search}`
    let site

    // get the site identifier from the url
    const {siteRef, localeRef} = getParamsFromPath(path)
    const sites = getSites()

    // step 1: use the siteRef to look for the site from the sites in the app config
    // since alias is optional, make sure it is defined before the equality check
    site = sites.find((site) => site.id === siteRef || (site.alias && site.alias === siteRef))
    if (site) {
        return site
    }

    // Step 2: if no siteRef but localeRef exists, find site by locale
    if (!siteRef && localeRef) {
        const normalizedLocaleRef = localeRef.toLowerCase()
        for (const candidateSite of sites) {
            const hasLocale = candidateSite.l10n.supportedLocales.some(
                (locale) =>
                    locale.id.toLowerCase() === normalizedLocaleRef ||
                    (locale.alias && locale.alias.toLowerCase() === normalizedLocaleRef)
            )
            if (hasLocale) {
                return candidateSite
            }
        }
    }

    // Step 3: if steps 1 and 2 do not work, use the defaultSite value to get the default site
    site = getDefaultSite()
    // Step 4: throw an error if site can't be found by any of the above steps
    if (!site) {
        throw new Error(
            "Can't find a matching default site. Please check your sites configuration."
        )
    }
    return site
}

/**
 * Returns the default site based on the defaultSite value from the app config
 * @returns {object} site - a site object from app config
 */
export const getDefaultSite = () => {
    const {app} = getConfig()
    const sites = getSites()

    if (sites.length === 1) {
        return sites[0]
    }

    return sites.find((site) => site.id === app.defaultSite)
}

/**
 * Return the list of sites that has included their respective aliases
 * @return {array} sites - list of sites including their aliases
 */
export const getSites = () => {
    const {sites = [], siteAliases = {}} = getConfig().app || {}

    if (!sites.length) {
        throw new Error("Can't find any sites from the config. Please check your configuration")
    }

    return sites.map((site) => {
        const alias = siteAliases[site.id]
        return {
            ...site,
            ...(alias ? {alias} : {})
        }
    })
}

export const getContinents = () => {
    const {app} = getConfig()
    if (app.continents)
        return Object.entries(app.continents).map(([continentName, locales]) => ({
            continentName,
            locales
        }))
}

/**
 * Given a site reference, return the site object
 * @param siteRef - site reference to look for the site object
 * @returns {object | undefined} found site object or default site object
 */
export const getSiteByReference = (siteRef) => {
    const defaultSite = getDefaultSite()
    const sites = getSites()

    return (
        sites.find((site) => {
            return site.alias === siteRef || site.id === siteRef
        }) || defaultSite
    )
}

/**
 * Utility function to get the static host URL based on the app configuration.
 * @returns {string}
 */
export const getStaticHost = () => {
    const {staticAPI: staticAPIConfig} = getConfig().app || {}
    return staticAPIConfig?.host
        ? `https://${staticAPIConfig.host}`
        : staticAPIConfig?.proxyPath
        ? `${getAppOrigin()}${staticAPIConfig.proxyPath}`
        : ''
}
